import { useState, useEffect } from 'react';
import type { QuizConfig, LLMConfig, QuestionType, Era, Difficulty, Subject } from '../types';
import { Settings, BookOpen, Brain, FileText, Key } from 'lucide-react';

const LLM_CONFIG_STORAGE_KEY = 'history-quiz-llm-config';
const LLM_CONFIGS_STORAGE_KEY = 'history-quiz-llm-configs';

type ProviderConfigs = Record<LLMConfig['provider'], Omit<LLMConfig, 'provider'>>;

const DEFAULT_PROVIDER_CONFIGS: ProviderConfigs = {
  openai: { apiKey: '', model: '' },
  grok: { apiKey: '', model: '' },
};

function buildLlmConfig(
  provider: LLMConfig['provider'],
  providerConfigs: ProviderConfigs
): LLMConfig {
  return {
    provider,
    apiKey: providerConfigs[provider].apiKey,
    model: providerConfigs[provider].model,
  };
}

interface QuizConfigProps {
  onSubmit: (config: QuizConfig, llmConfig: LLMConfig) => void;
  isLoading: boolean;
}

const QUESTION_TYPES: { value: QuestionType; label: string }[] = [
  { value: 'multiple_choice', label: '객관식' },
  { value: 'short_answer', label: '주관식' },
  { value: 'ox', label: 'OX' },
];

const SUBJECTS: { value: Subject; label: string }[] = [
  { value: 'history', label: '한국사' },
  { value: 'math', label: '수학' },
];

const HISTORY_ERAS: { value: Era; label: string }[] = [
  { value: 'prehistoric', label: '선사시대' },
  { value: 'gojoseon', label: '고조선' },
  { value: 'early_states', label: '원삼국' },
  { value: 'three_kingdoms', label: '삼국시대' },
  { value: 'unified_silla', label: '남북국' },
  { value: 'goryeo', label: '고려' },
  { value: 'joseon_early', label: '조선전기' },
  { value: 'joseon_late', label: '조선후기' },
  { value: 'enlightenment', label: '개화기' },
  { value: 'japanese_colonial', label: '일제강점기' },
  { value: 'modern', label: '현대' },
];

const MATH_TERMS: { value: Era; label: string }[] = [
  { value: 'elem6_1', label: '초6-1' },
  { value: 'elem6_2', label: '초6-2' },
  { value: 'mid1_1', label: '중1-1' },
  { value: 'mid1_2', label: '중1-2' },
  { value: 'mid2_1', label: '중2-1' },
  { value: 'mid2_2', label: '중2-2' },
  { value: 'mid3_1', label: '중3-1' },
  { value: 'mid3_2', label: '중3-2' },
];

const MATH_UNITS_BY_TERM: Record<string, string[]> = {
  elem6_1: ['분수의 나눗셈', '각기둥과 각뿔', '소수의 나눗셈', '비와 비율', '직육면체의 부피와 겉넓이'],
  elem6_2: ['원의 넓이', '분수의 나눗셈 활용', '비례식과 비례배분', '두 양의 관계', '쌓기나무'],
  mid1_1: ['소인수분해', '정수와 유리수', '문자와 식', '좌표평면', '기본도형'],
  mid1_2: ['일차방정식', '그래프와 비례', '평면도형', '입체도형', '자료의 정리와 해석'],
  mid2_1: ['유리수와 순환소수', '식의 계산', '일차부등식', '연립일차방정식', '일차함수'],
  mid2_2: ['도형의 성질', '피타고라스 정리', '확률', '경우의 수', '통계'],
  mid3_1: ['실수와 제곱근', '다항식의 인수분해', '이차방정식', '이차함수', '함수의 활용'],
  mid3_2: ['삼각비', '원의 성질', '공간도형', '통계', '표본조사'],
};

const DEFAULT_HISTORY_ERAS: Era[] = ['joseon_late', 'enlightenment', 'japanese_colonial'];
const DEFAULT_MATH_TERMS: Era[] = ['elem6_1', 'elem6_2', 'mid1_1'];

const DIFFICULTIES: { value: Difficulty; label: string }[] = [
  { value: 'easy', label: '쉬움' },
  { value: 'medium', label: '보통' },
  { value: 'hard', label: '어려움' },
];

export function QuizConfigPanel({ onSubmit, isLoading }: QuizConfigProps) {
  const [config, setConfig] = useState<QuizConfig>({
    subject: 'history',
    count: 10,
    difficulties: ['easy', 'medium', 'hard'],
    eras: DEFAULT_HISTORY_ERAS,
    mathUnits: [],
    types: ['multiple_choice'],
    title: '한국사 문제집',
    includeAnswerSheet: true,
    separateAnswerSheet: true,
  });

  const [selectedProvider, setSelectedProvider] = useState<LLMConfig['provider']>('openai');
  const [providerConfigs, setProviderConfigs] = useState<ProviderConfigs>(DEFAULT_PROVIDER_CONFIGS);
  const [isLlmConfigLoaded, setIsLlmConfigLoaded] = useState(false);
  const llmConfig = buildLlmConfig(selectedProvider, providerConfigs);

  // 저장된 LLM 설정 불러오기
  useEffect(() => {
    try {
      const savedByProvider = localStorage.getItem(LLM_CONFIGS_STORAGE_KEY);
      if (savedByProvider) {
        const parsed = JSON.parse(savedByProvider) as {
          selectedProvider?: LLMConfig['provider'];
          configs?: Partial<ProviderConfigs>;
        };
        const nextProvider = parsed.selectedProvider === 'grok' ? 'grok' : 'openai';
        const nextConfigs: ProviderConfigs = {
          openai: {
            apiKey: parsed.configs?.openai?.apiKey ?? '',
            model: parsed.configs?.openai?.model ?? '',
          },
          grok: {
            apiKey: parsed.configs?.grok?.apiKey ?? '',
            model: parsed.configs?.grok?.model ?? '',
          },
        };
        setSelectedProvider(nextProvider);
        setProviderConfigs(nextConfigs);
        return;
      }

      // 이전 단일 저장 포맷 마이그레이션
      const legacySaved = localStorage.getItem(LLM_CONFIG_STORAGE_KEY);
      if (legacySaved) {
        const parsed = JSON.parse(legacySaved) as LLMConfig;
        if (parsed.provider === 'openai' || parsed.provider === 'grok') {
          setSelectedProvider(parsed.provider);
          setProviderConfigs({
            ...DEFAULT_PROVIDER_CONFIGS,
            [parsed.provider]: {
              apiKey: parsed.apiKey || '',
              model: parsed.model || '',
            },
          });
        }
      }
    } catch (e) {
      console.error('Failed to load LLM config:', e);
    } finally {
      setIsLlmConfigLoaded(true);
    }
  }, []);

  // provider별 설정과 현재 provider 선택값 저장
  useEffect(() => {
    if (!isLlmConfigLoaded) return;
    try {
      localStorage.setItem(
        LLM_CONFIGS_STORAGE_KEY,
        JSON.stringify({
          selectedProvider,
          configs: providerConfigs,
        })
      );
    } catch (e) {
      console.error('Failed to save provider LLM configs:', e);
    }
  }, [selectedProvider, providerConfigs, isLlmConfigLoaded]);

  const handleEraToggle = (era: Era) => {
    setConfig(prev => {
      const eras = prev.eras.includes(era)
        ? prev.eras.filter(e => e !== era)
        : [...prev.eras, era];
      if (prev.subject !== 'math') {
        return { ...prev, eras };
      }

      const availableUnits = Array.from(new Set(eras.flatMap(term => MATH_UNITS_BY_TERM[term] ?? [])));
      const mathUnits = prev.mathUnits.filter(unit => availableUnits.includes(unit));
      return { ...prev, eras, mathUnits };
    });
  };

  const handleSubjectChange = (subject: Subject) => {
    setConfig(prev => ({
      ...prev,
      subject,
      eras: subject === 'history' ? DEFAULT_HISTORY_ERAS : DEFAULT_MATH_TERMS,
      mathUnits: [],
      title: subject === 'history' ? '한국사 문제집' : '수학 문제집',
    }));
  };

  const handleMathUnitToggle = (unit: string) => {
    setConfig(prev => {
      const mathUnits = prev.mathUnits.includes(unit)
        ? prev.mathUnits.filter(u => u !== unit)
        : [...prev.mathUnits, unit];
      return { ...prev, mathUnits };
    });
  };

  const handleTypeToggle = (type: QuestionType) => {
    setConfig(prev => {
      const types = prev.types.includes(type)
        ? prev.types.filter(t => t !== type)
        : [...prev.types, type];
      return { ...prev, types };
    });
  };

  const handleDifficultyToggle = (difficulty: Difficulty) => {
    setConfig(prev => {
      const difficulties = prev.difficulties.includes(difficulty)
        ? prev.difficulties.filter(d => d !== difficulty)
        : [...prev.difficulties, difficulty];
      return { ...prev, difficulties };
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (config.eras.length === 0) {
      alert(config.subject === 'history' ? '최소 하나의 시대를 선택해주세요.' : '최소 하나의 학년/학기를 선택해주세요.');
      return;
    }
    if (config.types.length === 0) {
      alert('최소 하나의 문제 유형을 선택해주세요.');
      return;
    }
    if (config.difficulties.length === 0) {
      alert('최소 하나의 난이도를 선택해주세요.');
      return;
    }
    if (!llmConfig.apiKey.trim()) {
      alert('API 키를 입력해주세요.');
      return;
    }
    onSubmit(config, { ...llmConfig, apiKey: llmConfig.apiKey.trim() });
  };

  return (
    <div className="max-w-2xl mx-auto p-4">
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4">
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <BookOpen className="w-6 h-6" />
            문제집 생성기
          </h1>
          <p className="text-indigo-100 text-sm mt-1">
            AI로 생성하는 맞춤형 문제집
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* LLM 설정 */}
          <div className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <Key className="w-5 h-5 text-indigo-600" />
              LLM API 설정
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  제공업체
                </label>
                <select
                  value={selectedProvider}
                  onChange={e => setSelectedProvider(e.target.value as LLMConfig['provider'])}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="openai">OpenAI (GPT-5.2)</option>
                  <option value="grok">Grok (xAI)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  모델 (선택사항)
                </label>
                <input
                  type="text"
                  placeholder={llmConfig.provider === 'openai' ? 'gpt-5.2' : 'grok-4.1-fast-non-reasoning'}
                  value={llmConfig.model}
                  onChange={e =>
                    setProviderConfigs(prev => ({
                      ...prev,
                      [selectedProvider]: {
                        ...prev[selectedProvider],
                        model: e.target.value,
                      },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                API 키
              </label>
              <input
                type="password"
                placeholder="sk-... (OpenAI) 또는 xai-... (Grok)"
                value={llmConfig.apiKey}
                onChange={e =>
                  setProviderConfigs(prev => ({
                    ...prev,
                    [selectedProvider]: {
                      ...prev[selectedProvider],
                      apiKey: e.target.value,
                    },
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                API 키는 브라우저에만 저장되며 서버로 전송되지 않습니다.
              </p>
            </div>
          </div>

          <hr className="border-gray-200" />

          {/* 기본 설정 */}
          <div className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <Settings className="w-5 h-5 text-indigo-600" />
              기본 설정
            </h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                과목
              </label>
              <div className="flex gap-2">
                {SUBJECTS.map(subject => (
                  <button
                    key={subject.value}
                    type="button"
                    onClick={() => handleSubjectChange(subject.value)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      config.subject === subject.value
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {subject.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                문제집 제목
              </label>
              <input
                type="text"
                value={config.title}
                onChange={e => setConfig(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  문제 수 ({config.count}개)
                </label>
                <input
                  type="range"
                  min="5"
                  max="50"
                  step="5"
                  value={config.count}
                  onChange={e => setConfig(prev => ({ ...prev, count: Number(e.target.value) }))}
                  className="w-full"
                />
              </div>
            </div>
          </div>

          {/* 시대/학기 선택 */}
          <div className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <Brain className="w-5 h-5 text-indigo-600" />
              {config.subject === 'history' ? '출제 시대' : '학년/학기'}
            </h2>
            <div className="flex flex-wrap gap-2">
              {(config.subject === 'history' ? HISTORY_ERAS : MATH_TERMS).map(era => (
                <button
                  key={era.value}
                  type="button"
                  onClick={() => handleEraToggle(era.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    config.eras.includes(era.value)
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {era.label}
                </button>
              ))}
            </div>
          </div>

          {config.subject === 'math' && (
            <div className="space-y-3">
              <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Brain className="w-5 h-5 text-indigo-600" />
                단원 선택 (선택사항)
              </h2>
              <div className="flex flex-wrap gap-2">
                {Array.from(new Set(config.eras.flatMap(term => MATH_UNITS_BY_TERM[term] ?? []))).map(unit => (
                  <button
                    key={unit}
                    type="button"
                    onClick={() => handleMathUnitToggle(unit)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      config.mathUnits.includes(unit)
                        ? 'bg-cyan-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {unit}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500">
                선택하지 않으면 해당 학년/학기의 전 범위에서 출제합니다.
              </p>
            </div>
          )}

          {/* 문제 유형 */}
          <div className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <FileText className="w-5 h-5 text-indigo-600" />
              문제 유형
            </h2>
            <div className="flex flex-wrap gap-2">
              {QUESTION_TYPES.map(type => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => handleTypeToggle(type.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    config.types.includes(type.value)
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          {/* 난이도 */}
          <div className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <Brain className="w-5 h-5 text-indigo-600" />
              난이도
            </h2>
            <div className="flex flex-wrap gap-2">
              {DIFFICULTIES.map(difficulty => (
                <button
                  key={difficulty.value}
                  type="button"
                  onClick={() => handleDifficultyToggle(difficulty.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    config.difficulties.includes(difficulty.value)
                      ? 'bg-emerald-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {difficulty.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500">
              여러 난이도를 함께 선택하면 선택한 난이도들로 균형 분배합니다.
            </p>
          </div>

          {/* PDF 옵션 */}
          <div className="bg-gray-50 p-4 rounded-lg space-y-3">
            <h3 className="font-medium text-gray-800">PDF 출력 옵션</h3>
            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={config.includeAnswerSheet}
                  onChange={e => setConfig(prev => ({ ...prev, includeAnswerSheet: e.target.checked }))}
                  className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700">답안지 포함</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={config.separateAnswerSheet}
                  onChange={e => setConfig(prev => ({ ...prev, separateAnswerSheet: e.target.checked }))}
                  className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700">답안지를 별도 PDF로 분리</span>
              </label>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-indigo-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                문제 생성 중...
              </>
            ) : (
              '문제집 생성하기'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
